import { useState, useEffect, ReactElement } from 'react';

import api from '~/services/api';
import { useStoreState, useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';
import Modal from '~/ui/components/common/Modal';
import Loader from '~/ui/components/common/Loader';
import { IIdName } from '~/services/api/types';
import { UserRole } from '~/types';
import AssociatedProgramsTable from './componets/AssociatedProgramsTable';
import AvailableProgramsTable from './componets/AvailableProgramsTable';
import styles from './ProgramModal.module.scss';

interface IProps {
  open: boolean;
  onClose: () => void;
  userId: number;
}

const ProgramModal = ({ open, onClose, userId }: IProps): ReactElement => {
  const [loading, setLoading] = useState(true);
  const [availablePrograms, setAvailablePrograms] = useState<IIdName[]>([]);

  const associatedPrograms = useStoreState(state => state.users.userAssociatedPrograms);

  const showError = useStoreActions(actions => actions.snackbar.showError);
  const setUserAssociatedPrograms = useStoreActions(
    actions => actions.users.setUserAssociatedPrograms,
  );

  const onMount = async () => {
    setLoading(true);

    try {
      const [selectors, programs] = await Promise.all([
        api.users.getSelectorsById(0, UserRole.HealthManager).then(res => res.data),
        api.users.getAssociatedPrograms(userId).then(res => res.data),
      ]);

      setUserAssociatedPrograms(programs);
      setAvailablePrograms(
        selectors.programs.filter(
          program =>
            !programs.some(associatedProgram => associatedProgram.programId === program.id),
        ),
      );
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    onMount();
  }, []);

  return (
    <Modal
      contentIdAttribute="scrollableDiv"
      open={open}
      title="Programs"
      onClose={onClose}
      maxWidth="md"
      className={styles.modal}
    >
      {loading ? (
        <Loader />
      ) : (
        <div>
          <h3>Associated Programs</h3>
          <AssociatedProgramsTable
            currentUserId={userId}
            programUsers={associatedPrograms}
            onRefresh={onMount}
          />
          <h3>Available Programs</h3>
          <AvailableProgramsTable
            currentUserId={userId}
            availablePrograms={availablePrograms}
            onRefresh={onMount}
          />
        </div>
      )}
    </Modal>
  );
};

export default ProgramModal;
